import { useMemo } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { IconButton as IconButtonV1 } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { type Kpi } from "../../explore-budgets/client-setting/types";
import { KpiSelect } from "../../shared/kpi-select";
import { useChannelComparisonModal } from "../hooks/use-channel-comparison-modal";
import { useModelMetadataKpis } from "../hooks/use-model-kpis";
import { useSelectedChannels } from "../hooks/use-selected-channels";
import { KpiPeriodSelector } from "../kpi-period-selector";
import { curateHistoricalDataset } from "../model-fit-chart/utils/export-to-csv";

import { IncrementalRoasChart } from "./incremental-roas-chart";
import { IncrementalSpendChart } from "./incremental-spend-chart";
import { type DataColumn } from "./incremental-spend-chart/const";

interface Props {
  columns: DataColumn[];
  costCurvesAllTimeData: any;
  costCurvesAllTimeByQuantileData: any;
  historicalContributionsData: any;
  weeklySpendsByChannel: Record<string, number[]>;
}

const sumKpiData = (data: any, kpiName: string) =>
  (data as any).reduce(
    (acc: number, row: any) =>
      acc + (row?.kpiData?.[kpiName] ?? 0) + (row?.promoData?.[kpiName] ?? 0),
    0,
  );

export const ChannelComparisonsModal = ({
  columns,
  costCurvesAllTimeData,
  costCurvesAllTimeByQuantileData,
  historicalContributionsData,
  weeklySpendsByChannel,
}: Props) => {
  const {
    isChannelComparisonModalOpen,
    setIsChannelComparisonModalOpen,
    selectedChannelTabIndex,
    setSelectedChannelTabIndex,
    channelComparisonKpi,
    setChannelComparisonKpi,
    channelComparisonDateRange,
    setChannelComparisonDateRange,
  } = useChannelComparisonModal();
  const { kpis } = useModelMetadataKpis();
  const { addToast } = useToasts();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { designElementManager } = useDesignElementManager();
  const { selectedChannels, singleSelectedChannel } = useSelectedChannels();
  const { user } = useUser();
  const {
    kpiFormatter,
    efficiencyKpiType,
    efficiencyKpiFormatter,
    efficiencyKpiCalculator,
  } = efficiencyKpiManager(channelComparisonKpi as unknown as Kpi, user);

  const currentColumn = useMemo<DataColumn | null>(() => {
    const columnName =
      singleSelectedChannel || selectedChannels[selectedChannelTabIndex];
    return columns.find((e) => e.column_name === columnName) ?? null;
  }, [
    selectedChannelTabIndex,
    singleSelectedChannel,
    selectedChannels,
    columns,
  ]);

  const selectedColumns = useMemo(
    () =>
      selectedChannels
        .map((channel) => columns.find((e) => e.column_name === channel))
        .filter(Boolean) as DataColumn[],
    [selectedChannels, columns],
  );

  if (!currentColumn || !channelComparisonKpi) {
    return null;
  }

  const firstBreakdown = currentColumn.breakdowns?.[0]?.value;
  const rows = curateHistoricalDataset(
    historicalContributionsData[currentColumn.column_name],
    channelComparisonDateRange,
  );

  const sumOfSpendOnChannel = rows.reduce(
    (acc: number, data: any) => acc + data.spend,
    0,
  );

  const sumOfKpiOnChannel = sumKpiData(rows, channelComparisonKpi.kpi_name);

  const shouldShowChannelTabs =
    selectedColumns.length > 1 && !singleSelectedChannel;

  return (
    <Modal
      size="3xl"
      isOpen={isChannelComparisonModalOpen}
      onClose={() => setIsChannelComparisonModalOpen(false)}
      headerProps={{ className: "px-0 py-0" }}
      headerTitleProps={{
        className: "w-full shadow-[0px_5px_8px_0px_rgba(2,_18,_48,_0.12)]",
      }}
      hideCloseButton
      bodyClassName="px-0 py-0"
      titleContent={
        <>
          {shouldShowChannelTabs && (
            <div className="flex items-center gap-1.5 bg-gray+1">
              {selectedColumns.map((column, index) => (
                <div
                  key={column.column_name}
                  className={clsx(
                    "flex items-center py-2.5 px-5 rounded-t-md last:rounded-r-none",
                    "whitespace-nowrap truncate text-sm w-full min-w-0",
                    {
                      "bg-gray-2 hover:bg-cosmic-4 hover:text-cosmic cursor-pointer":
                        column.column_name !== currentColumn.column_name,
                      "bg-v2-primary":
                        column.column_name === currentColumn.column_name,
                    },
                  )}
                  onClick={() => setSelectedChannelTabIndex(index)}
                >
                  <div>
                    {designElementManager
                      .getDesignElement(
                        column.breakdowns?.[0]?.value || column.label,
                      )
                      .getComponent({
                        size: "100%",
                        className: "w-5 h-5 mr-1",
                      })}
                  </div>
                  <Tooltip
                    className="truncate"
                    content={column.label}
                    placement="top"
                    noInfoCircle
                  >
                    <div className="truncate">{column.label}</div>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center font-semibold h-20 px-8 bg-v2-primary">
            <div
              className={clsx("flex items-center gap-2 min-w-0 truncate", {
                "text-gray+2": currentColumn.breakdowns.length > 1,
              })}
            >
              <span>
                {designElementManager
                  .getDesignElement(firstBreakdown || currentColumn.label)
                  .getComponent({ size: "100%", className: "w-10 h-10 mr-1" })}
              </span>
              <span className="truncate">{firstBreakdown}</span>
              {currentColumn.breakdowns.length > 1 && (
                <>
                  <span>/</span>
                  <span className="text-v2-primary truncate">
                    {currentColumn.breakdowns
                      .slice(1)
                      .map(({ value }) => value)
                      .join(" / ")}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-3 pr-10 relative">
              <div>
                <div className="mb-1 text-xs">
                  <span className="font-medium text-interstellar+2 mr-2 tracking-wide">
                    OPTIMIZE FOR
                  </span>
                </div>
                <KpiSelect
                  className="w-80"
                  value={channelComparisonKpi.kpi_name}
                  options={kpis.map((e) => ({
                    label: e.friendly_name,
                    value: e.kpi_name,
                  }))}
                  onChange={(value) =>
                    setChannelComparisonKpi(
                      kpis.find((e) => e.kpi_name === value)!,
                    )
                  }
                />
              </div>
              <div className="border-l h-8 self-end"></div>
              <KpiPeriodSelector
                value={channelComparisonDateRange}
                onChange={(value) => {
                  trackMMMEvent({
                    component:
                      ModelInsightsTrackedComponent.INCREMENTAL_ROAS_PERIOD_SELECTOR,
                    action: "onChange",
                  });
                  setChannelComparisonDateRange(value);
                }}
              />
              <div className="border-l h-8 self-end"></div>
              <IconButtonV1
                variant="secondary"
                leftIcon={
                  <i className="fa-sharp fa-regular fa-link mr-1.5 text-sm" />
                }
                className="mt-4"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    addToast("Copied to clipboard!", { appearance: "success" });
                  } catch (err) {
                    addToast("Error while coping to clipboard!", {
                      appearance: "error",
                    });
                  }
                }}
              >
                COPY LINK
              </IconButtonV1>
              <button
                className="absolute -top-3 -right-6 px-4 py-2"
                onClick={() => setIsChannelComparisonModalOpen(false)}
              >
                <i className="fa-regular fa-times text-lg" />
              </button>
            </div>
          </div>
        </>
      }
    >
      <div className="bg-v2-primary p-8">
        <div className="flex items-center gap-2.5 mb-5 whitespace-nowrap">
          <div className="flex justify-between items-center p-5 flex-grow rounded-lg bg-cosmic-4">
            <div className="text-xl font-semibold">
              {channelComparisonKpi.friendly_name}
            </div>
            <div className="text-cosmic text-4xl md:text-2xl">
              {kpiFormatter(sumOfKpiOnChannel)}
            </div>
          </div>
          <div className="flex justify-between items-center p-5 flex-grow rounded-lg bg-cosmic-4">
            <div className="text-xl font-semibold">Spend</div>
            <div className="text-cosmic text-4xl md:text-2xl">
              {MetricFormatters.currency(
                sumOfSpendOnChannel,
                user.currencies.currentCurrency,
                { precision: "approximate" },
              )}
            </div>
          </div>
          <div className="flex justify-between items-center p-5 flex-grow rounded-lg bg-cosmic-4">
            <div className="text-xl font-semibold">
              Total i{efficiencyKpiType}
            </div>
            <div className="text-cosmic text-4xl md:text-2xl">
              {efficiencyKpiFormatter(
                efficiencyKpiCalculator(sumOfSpendOnChannel, sumOfKpiOnChannel),
              )}
            </div>
          </div>
        </div>
        <IncrementalSpendChart
          columnName={currentColumn.column_name}
          costCurvesAllTimeData={costCurvesAllTimeData}
          costCurvesAllTimeByQuantileData={costCurvesAllTimeByQuantileData}
          selectedKpi={channelComparisonKpi}
          column={currentColumn}
          historicalContributionsData={historicalContributionsData}
          weeklySpends={weeklySpendsByChannel[currentColumn.column_name] ?? []}
          dateRange={channelComparisonDateRange}
        />
        <IncrementalRoasChart
          columnName={currentColumn.column_name}
          historicalContributionsData={historicalContributionsData}
          selectedKpi={channelComparisonKpi}
          column={currentColumn}
          dateRange={channelComparisonDateRange}
        />
      </div>
    </Modal>
  );
};
